import { FunctionComponent, ReactElement, ChangeEvent, useMemo } from 'react';
import { Stack } from '@mui/material';
import FormDialog from '@/components/ui/form-dialog';
import { CreateCustomerRequest, UpdateCustomerRequest } from '@/api/customers/types';
import { useCreateCustomer, useUpdateCustomer } from '@/queries/customers';
import TextField from '@/components/ui/form-inputs/TextField';
import SelectField from '@/components/ui/form-inputs/SelectField';
import { CUSTOMER_FORM_KEY } from '@/shared/constants/form-keys';
import { useTimezones } from '@/queries/timezones';
import { pushToDataLayer, ContentGroup } from '@/shared/utils/analytics';

export type CustomerFormModel = CreateCustomerRequest | UpdateCustomerRequest;

interface CustomerFormPops {
	open: boolean;
	onClose: () => void;
	formModel: CustomerFormModel;
	updateFormModel: (field: keyof CustomerFormModel, value: string) => void;
	customerId?: string;
	onSubmitted?: () => void;
}

const CustomerForm: FunctionComponent<CustomerFormPops> = ({
	open,
	onClose,
	formModel,
	updateFormModel,
	customerId,
	onSubmitted,
}): ReactElement => {
	const { data: timezones } = useTimezones();
	const title = useMemo(() => (customerId ? 'Edit customer' : 'Create new customer'), [customerId]);
	const submitLabel = useMemo(() => (customerId ? 'Save' : 'Create customer'), [customerId]);
	const disabled = useMemo(() => {
		const { name, timeZone } = formModel;
		return name.length === 0 || timeZone.length === 0;
	}, [formModel]);

	const trackCreateCustomer = () => {
		pushToDataLayer({
			event: 'customer_created',
			content_group: ContentGroup.Customers,
			customer_name: formModel.name,
		});
	};

	const trackEditCustomer = () => {
		pushToDataLayer({
			event: 'customer_edited',
			content_group: ContentGroup.Customers,
			customer_name: formModel.name,
		});
	};

	const { isLoading: isCreateCustomerSubmitting, mutate: onCreateCustomerSubmit } =
		useCreateCustomer({
			onSuccess: () => {
				onSubmitted?.();
				onClose();
				trackCreateCustomer();
			},
		});

	const { isLoading: isUpdateCustomerSubmitting, mutate: onUpdateCustomerSubmit } =
		useUpdateCustomer({
			onSuccess: () => {
				onSubmitted?.();
				onClose();
				trackEditCustomer();
			},
		});

	const onSubmit = () => {
		const request = { request: formModel, formKey: CUSTOMER_FORM_KEY };
		if (customerId) {
			onUpdateCustomerSubmit({ id: customerId, request });
		} else {
			onCreateCustomerSubmit({ request });
		}
	};

	return (
		<FormDialog
			formKey={CUSTOMER_FORM_KEY}
			open={open}
			onClose={onClose}
			title={title}
			fullWidth
			maxWidth="xs"
			onSubmit={onSubmit}
			actions={{
				cancel: {
					label: 'Cancel',
				},
				confirm: {
					label: submitLabel,
					isLoading: isCreateCustomerSubmitting || isUpdateCustomerSubmitting,
					disabled,
				},
			}}
		>
			<Stack direction="column" spacing={2}>
				<TextField
					id="name"
					name="name"
					label="Customer name"
					formKey={CUSTOMER_FORM_KEY}
					required
					fullWidth
					value={formModel.name}
					onChange={(event: ChangeEvent<HTMLInputElement>) =>
						updateFormModel('name', event.target.value)
					}
				/>
				<SelectField
					id="timeZone"
					name="name"
					label="Time zone"
					formKey={CUSTOMER_FORM_KEY}
					required
					fullWidth
					value={formModel.timeZone}
					onChange={(event: ChangeEvent<HTMLInputElement>) =>
						updateFormModel('timeZone', event.target.value)
					}
					options={timezones?.map((timezone) => ({ value: timezone, label: timezone }))}
				/>
			</Stack>
		</FormDialog>
	);
};

export default CustomerForm;
